import moment from "moment";

/**
 * 获取最近splitNum时间
 * @param value 
 * @param splitNum 
 * @returns 
 */
export const getMinute = (value: moment.MomentInput, splitNum = 5) => {
  const now = moment(value, "HH:mm");
  // 取当前分钟数
  const minutes = now.minutes();
  // 计算多出的部分
  const excess = minutes % splitNum;
  // 减去多出的部分得到最近的5分钟倍数的时间
  const time = now.subtract(excess, 'minutes');
  return time.format('HH:mm:ss');
};

/**
 * 异动分析过滤选择分钟数据
 * @param arr 趋势图数据
 * @param changeTime 分钟筛选器时间
 * @returns 截取时间段的趋势图数据
 */
export const formatTimeList = (arr = [], changeTime: string) => {
  // 趋势图截取到分钟选择的时间
  const list = arr?.filter((item: any) => moment(item?.time, "HH:mm:ss").isSameOrBefore(moment(changeTime, "HH:mm:ss")));
  return list || [];
};

/**
 * 数据过滤配置接口
 */
export interface IFilterDataConfig {
  [key: string]: number;
}

/**
 * 过滤数据点的方法
 * 根据filterDataObj配置，对指定字段的数据进行间隔过滤
 * @param response 原始响应数据
 * @param filterDataObj 过滤配置对象，key为字段名，value为间隔数
 * @param filterFields 需要过滤的字段数组，默认为['line_dot', 'line_dot_last', 'line_dot_contrast']
 * @returns 过滤后的响应数据
 */
export const filterResponseData = (
  response: any,
  filterDataObj: IFilterDataConfig,
  filterFields: string[] = ['line_dot', 'line_dot_last', 'line_dot_contrast'],

): any => {
  if (!response || !filterDataObj || Object.keys(filterDataObj).length === 0) {
    return response;
  }

  const filteredResponse = { ...response };

  // 遍历需要过滤的字段
  Object.keys(filterDataObj).forEach((fieldKey) => {
    const interval = filterDataObj?.[fieldKey];
    if (!filteredResponse[fieldKey] || typeof interval !== 'number' || interval <= 0) {
      return;
    }
    // 对每个需要过滤的字段进行处理
    filterFields.forEach((filterField) => {
      if (filteredResponse?.[fieldKey]?.[filterField] && Array.isArray(filteredResponse?.[fieldKey]?.[filterField])) {
        const originalArray = filteredResponse?.[fieldKey]?.[filterField] || [];
        const filteredArray: any[] = [];
        // 按照间隔过滤数据：保留第一个，然后每隔interval个保留一个
        for (let i = 0;i < originalArray.length;i++) {
          if (i === 0 || i % (interval + 1) === 0) {
            filteredArray.push(originalArray?.[i]);
          }
        }
        filteredResponse[fieldKey][filterField] = filteredArray;
      }
    });
  });

  return filteredResponse;
};

/**
 * 通用：根据 filterDataObj 针对多个 Top 列表键做间隔过滤
 * 例如 filterDataObj = { active_users_top: 2, new_users_top: 3 }
 * 将会对 response.active_users_top 和 response.new_users_top 的 list 按对应间隔过滤
 * @param response 原始响应数据
 * @param filterDataObj 键为需要处理的数据字段名（如 active_users_top），值为间隔数
 * @param listField 列表字段名，默认 'list'
 * @returns 过滤后的响应数据
 */
export const filterTopListsByConfig = (
  response: any,
  filterDataObj: IFilterDataConfig,
  listField = 'list',
): any => {
  if (!response || !filterDataObj || Object.keys(filterDataObj).length === 0) {
    return response;
  }

  const filteredResponse = { ...response };

  Object.keys(filterDataObj).forEach((fieldKey) => {
    const interval = filterDataObj?.[fieldKey];

    if (!Array.isArray(filteredResponse?.[fieldKey]) || typeof interval !== 'number' || interval <= 0) {
      return;
    }

    filteredResponse[fieldKey] = filteredResponse[fieldKey].map((item: any) => {
      const originalList: any[] = Array.isArray(item?.[listField]) ? item[listField] : [];
      const filteredList: any[] = [];

      for (let i = 0;i < originalList.length;i++) {
        if (i === 0 || i % (interval + 1) === 0) {
          filteredList.push(originalList[i]);
        }
      }

      return {
        ...item,
        [listField]: filteredList,
      };
    });
  });

  return filteredResponse;
};


/**
 * 通用：对扁平结构的列表（如 active_users_list/new_users_list）按 name 分组进行间隔过滤
 * - 保持整体原有顺序不变
 * - 对相同 name 的条目，采用与 filterTopListsByConfig 相同的策略：保留第一条，然后每隔 interval 个保留一条
 * @param response 原始响应数据
 * @param filterDataObj 键为需要处理的数据字段名（如 active_users_list），值为间隔数
 * @param groupKey 分组字段名，默认 'name'
 * @returns 过滤后的响应数据
 */
export const filterFlatListsByNameGroup = (
  response: any,
  filterDataObj: IFilterDataConfig,
  groupKey = 'name',
): any => {
  if (!response || !filterDataObj || Object.keys(filterDataObj).length === 0) {
    return response;
  }

  const filteredResponse = { ...response };

  Object.keys(filterDataObj).forEach((fieldKey) => {
    const interval = filterDataObj?.[fieldKey];
    const originalList: any[] = Array.isArray(filteredResponse?.[fieldKey]) ? filteredResponse[fieldKey] : [];

    if (!Array.isArray(originalList) || typeof interval !== 'number' || interval <= 0) {
      return;
    }

    const result: any[] = [];
    const groupIndexMap: Record<string, number> = {};
    for (let i = 0;i < originalList.length;i++) {
      const item = originalList[i];
      const groupValue = item?.[groupKey];
      const prevIndexInGroup = groupIndexMap[groupValue] ?? -1;
      const nextIndexInGroup = prevIndexInGroup + 1;

      if (nextIndexInGroup === 0 || nextIndexInGroup % (interval + 1) === 0) {
        result.push(item);
      }

      groupIndexMap[groupValue] = nextIndexInGroup;
    }

    filteredResponse[fieldKey] = result;
  });
  return filteredResponse;
};

export const formatByDimensionCard = (response: any, dataKey: string) => {
  const data = response?.[dataKey] || {};
  const {
    current_time: currentTime,
    current_count: currentCount,
    dod_time: dodTime,
    dod_count: dodCount,
    sdlw_time: sdlwTime,
    sdlw_count: sdlwCount,
    dod,
    sdlw
  } = data?.card || {};
  const newResponse = {
    breakdown: data?.breakdown || [],
    card_list: [
      {
        metrics_name: dataKey,
        value: currentCount,
        dod: {
          show_value: dod,
          this_time: currentTime,
          this_value: currentCount,
          last_time: dodTime,
          last_value: dodCount
        },
        sdlw: {
          show_value: sdlw,
          this_time: currentTime,
          this_value: currentCount,
          last_time: sdlwTime,
          last_value: sdlwCount
        }
      }
    ],
  };
  return newResponse;
};

interface Entry {
  time: string;
  value: string;
}

interface Country {
  name: string;
  list: Entry[];
}

// 计算ua分维度接口占比数据
export const getUaDimensionShareData = (originalResponse: any, dataKey: string) => {
  if (!dataKey) return originalResponse;
  const response = originalResponse?.[dataKey] || {};
  // 占比计算
  const shareDataObj: any = {};
  Object.keys(response).forEach((key) => {
    const shareData: { [time: string]: any; } = {};
    if (key=== "top") { // top数据
      // 处理share汇总值
      response?.[key]?.forEach((country: Country) => {
        country?.list?.forEach((each: Entry) => {
          const { time, value } = each;
          if (!shareData?.[time]) {
            shareData[time] = parseFloat(value) || 0;
          } else {
            shareData[time] += parseFloat(value) || 0;
          }
        });
      });
      shareDataObj[dataKey] = shareData;
    }
  });
  Object.keys(response).forEach((key) => {
    if (key=== "top") { // top数据
      const shareData = shareDataObj?.[dataKey];
      // 处理share值
      response?.[key]?.forEach((val: Country) => {
        const { list } = val;
        val.list = list?.map((each) => ({
          ...each,
          share: parseFloat(each?.value) / parseFloat(shareData?.[each?.time]) || '-'
        }));
      });
    }
    if (key=== "list") { // list数据
      const shareData = shareDataObj?.[dataKey];
      // 处理share值
      response[key] = response?.[key]?.map((val: Entry) => ({
        ...val,
        share: parseFloat(val.value) / parseFloat(shareData?.[val?.time]) || '-'
      }));
    }
  });
  return originalResponse;
};